import React, { Component } from 'react';
import { Modal, Button, Form, Row, Col, Card, Badge, InputGroup } from 'react-bootstrap';
import { FaRandom } from 'react-icons/fa';

class C3DInfoModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formData: {}
    };
  }

  componentDidMount() {
    if (this.props.c3dInfo) {
      this.setState({
        formData: { ...this.props.c3dInfo }
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.c3dInfo !== this.props.c3dInfo && this.props.c3dInfo) {
      this.setState({
        formData: { ...this.props.c3dInfo }
      });
    }
  }

  handleInputChange = (field, value) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [field]: value
      }
    }));
  }

  // Generate random 9-digit CMT number starting with 1
  generateCMTNumber = () => {
    // Generate 8 random digits (0-9)
    const randomDigits = Math.floor(Math.random() * 90000000) + 10000000;
    // Combine with leading 1 to create 9-digit number
    const cmtNumber = '1' + randomDigits.toString();
    this.handleInputChange('cmt_number', cmtNumber);
  }

  // Generate random C3D issue date (2023 or 2024)
  generateC3DIssueDate = () => {
    // Randomly choose between 2023 or 2024
    const year = Math.random() < 0.5 ? 2023 : 2024;
    
    // Generate random month (1-12)
    const month = Math.floor(Math.random() * 12) + 1;
    
    // Generate random day based on month
    let maxDay;
    if (month === 2) {
      // February - check for leap year
      maxDay = ((year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)) ? 29 : 28;
    } else if ([4, 6, 9, 11].includes(month)) {
      // April, June, September, November
      maxDay = 30;
    } else {
      // Other months
      maxDay = 31;
    }
    
    const day = Math.floor(Math.random() * maxDay) + 1;
    
    // Format as YYYY-MM-DD for date input
    const issueDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    this.handleInputChange('c3d_issue_date', issueDate);
  }

  // Generate random C3D expired date (2025 or 2026)
  generateC3DExpiredDate = () => {
    // Randomly choose between 2025 or 2026
    const year = Math.random() < 0.5 ? 2025 : 2026;
    
    // Generate random month (1-12)
    const month = Math.floor(Math.random() * 12) + 1;
    
    // Generate random day based on month
    let maxDay;
    if (month === 2) {
      // February - check for leap year
      maxDay = ((year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)) ? 29 : 28;
    } else if ([4, 6, 9, 11].includes(month)) {
      // April, June, September, November
      maxDay = 30;
    } else {
      // Other months
      maxDay = 31;
    }
    
    const day = Math.floor(Math.random() * maxDay) + 1;
    
    // Format as YYYY-MM-DD for date input
    const expiredDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    this.handleInputChange('c3d_expired_date', expiredDate);
  }

  // Format date for HTML date input (YYYY-MM-DD), handling timezone conversion
  formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // If already in DD/MM/YYYY format, convert to YYYY-MM-DD
    if (typeof dateString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }
    
    // Try to parse as ISO date or other formats
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return ''; // Return empty if can't parse
      }
      
      // Convert to Hanoi timezone (UTC+7) for input
      const hanoiDate = new Date(date.getTime() + (7 * 60 * 60 * 1000));
      return hanoiDate.toISOString().split('T')[0];
    } catch (error) {
      return ''; // Return empty if error
    }
  }

  // Format date to DD/MM/YYYY for display, handling timezone conversion
  formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    
    // If already in DD/MM/YYYY format, return as is
    if (typeof dateString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    
    // Try to parse as ISO date or other formats
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if can't parse
      }
      
      // Convert to Hanoi timezone (UTC+7) for display
      const hanoiDate = new Date(date.getTime() + (7 * 60 * 60 * 1000));
      return hanoiDate.toLocaleDateString('en-GB');
    } catch (error) {
      return dateString; // Return original if error
    }
  }

  // Map gender from English to Vietnamese
  mapGender = (gender) => {
    if (!gender) return '-';
    switch (gender.toUpperCase()) {
      case 'MALE':
        return 'Nam';
      case 'FEMALE':
        return 'Nữ';
      default:
        return gender;
    }
  }

  // Map Etsy status to Vietnamese
  mapEtsyStatus = (status) => {
    if (!status) return '-';
    switch (status.toUpperCase()) {
      case 'NO_TAX_INFO':
        return 'Chưa có thông tin thuế';
      case 'WAIT_FOR_TRUSTED_INFO':
        return 'Chờ thông tin người tin cậy';
      case 'FOUND_TRUSTED_INFO':
        return 'Đã tìm thấy người tin cậy';
      case 'REGISTERED':
        return 'Đã đăng ký';
      case 'SUSPENDED':
        return 'Bị tạm dừng';
      case 'CANCEL':
        return 'Đã hủy';
      default:
        return status;
    }
  }

  render() {
    const { 
      show, 
      onHide, 
      c3dInfo, 
      isEditMode, 
      onSave,
      refTrustedPersonMid = null 
    } = this.props;

    if (!c3dInfo) return null;

    const { formData } = this.state;

    return (
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Chỉnh sửa' : 'Xem'} thông tin C3D - {c3dInfo.full_name || 'N/A'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            {/* Basic Information */}
            <Row className="mb-4">
              <Col>
                <h5 className="text-primary">Thông tin cơ bản</h5>
                <hr />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md="6">
                <Form.Group>
                  <Form.Label>Tên đầy đủ</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.full_name || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('full_name', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Tên tiếng Anh</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.full_name_english || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('full_name_english', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* C3D Information */}
            <Row className="mb-4">
              <Col>
                <h5 className="text-primary">Thông tin C3D</h5>
                <hr />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md="3">
                <Form.Group>
                  <Form.Label>Số C3D</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.c3d_number || ''} 
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <Form.Label>Số CMT</Form.Label>
                  {isEditMode ? (
                    <InputGroup>
                      <Form.Control 
                        type="text" 
                        value={formData.cmt_number || ''} 
                        onChange={(e) => this.handleInputChange('cmt_number', e.target.value)}
                        placeholder="Nhập số CMT"
                      />
                      <Button 
                        variant="outline-secondary" 
                        onClick={this.generateCMTNumber}
                        title="Tạo số CMT ngẫu nhiên"
                      >
                        <FaRandom size={12} />
                      </Button>
                    </InputGroup>
                  ) : (
                    <Form.Control 
                      type="text" 
                      value={formData.cmt_number || ''} 
                      readOnly
                    />
                  )}
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <Form.Label>Ngày sinh</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.c3d_dob || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('c3d_dob', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <Form.Label>Ngày cấp C3D</Form.Label>
                  {isEditMode ? (
                    <InputGroup>
                      <Form.Control 
                        type="date"
                        value={this.formatDateForInput(formData.c3d_issue_date)} 
                        onChange={(e) => this.handleInputChange('c3d_issue_date', e.target.value)}
                      />
                      <Button 
                        variant="outline-secondary" 
                        onClick={this.generateC3DIssueDate}
                        title="Tạo ngày cấp C3D ngẫu nhiên (2023/2024)"
                      >
                        <FaRandom size={12} />
                      </Button>
                    </InputGroup>
                  ) : (
                    <Form.Control 
                      type="text"
                      value={this.formatDateForDisplay(formData.c3d_issue_date)} 
                      readOnly
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md="3">
                <Form.Group>
                  <Form.Label>Ngày hết hạn C3D</Form.Label>
                  {isEditMode ? (
                    <InputGroup>
                      <Form.Control 
                        type="date"
                        value={this.formatDateForInput(formData.c3d_expired_date)} 
                        onChange={(e) => this.handleInputChange('c3d_expired_date', e.target.value)}
                      />
                      <Button 
                        variant="outline-secondary" 
                        onClick={this.generateC3DExpiredDate}
                        title="Tạo ngày hết hạn C3D ngẫu nhiên (2025/2026)"
                      >
                        <FaRandom size={12} />
                      </Button>
                    </InputGroup>
                  ) : (
                    <Form.Control 
                      type="text"
                      value={this.formatDateForDisplay(formData.c3d_expired_date)} 
                      readOnly
                    />
                  )}
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <Form.Label>Giới tính</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={isEditMode ? (formData.c3d_sex || '') : this.mapGender(formData.c3d_sex)} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('c3d_sex', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <Form.Label>Nơi sinh</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.c3d_place_of_origin || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('c3d_place_of_origin', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <Form.Label>Nơi cư trú</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.c3d_place_of_residence || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('c3d_place_of_residence', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md="4">
                <Form.Group>
                  <Form.Label>Thành phố</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.c3d_city || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('c3d_city', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Tax Information */}
            <Row className="mb-4">
              <Col>
                <h5 className="text-primary">Thông tin thuế</h5>
                <hr />
              </Col>
            </Row>
            
            {/* DN Tax Info */}
            {formData.DN_tax_infos && formData.DN_tax_infos.length > 0 && (
              <Row className="mb-3">
                <Col>
                  <h6>Thuế kinh doanh</h6>
                  {formData.DN_tax_infos.map((tax, index) => (
                    <Card key={index} className="mb-2">
                      <Card.Body>
                        <Row>
                          <Col md="6">
                            <strong>Tên người nộp:</strong> {tax.payer_name || '-'}<br />
                            <strong>Địa chỉ kinh doanh:</strong> {tax.business_address || '-'}<br />
                            <strong>Cơ quan thuế:</strong> {tax.tax_authority || '-'}
                          </Col>
                          <Col md="6">
                            <strong>Thành phố:</strong> {tax.city || '-'}<br />
                            <strong>Trạng thái:</strong> {tax.status_text || '-'}<br />
                            <strong>Mã trạng thái:</strong> {tax.status || '-'}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </Col>
              </Row>
            )}

            {/* TNCN Tax Info */}
            {formData.TNCN_tax_infos && formData.TNCN_tax_infos.length > 0 && (
              <Row className="mb-3">
                <Col>
                  <h6>Thuế TNCN</h6>
                  {formData.TNCN_tax_infos.map((tax, index) => (
                    <Card key={index} className="mb-2">
                      <Card.Body>
                        <Row>
                          <Col md="6">
                            <strong>Tên người nộp:</strong> {tax.payer_name || '-'}<br />
                            <strong>Cơ quan thuế:</strong> {tax.tax_authority || '-'}
                          </Col>
                          <Col md="6">
                            <strong>Thành phố:</strong> {tax.city || '-'}<br />
                            <strong>Trạng thái:</strong> {tax.status_text || '-'}<br />
                            <strong>Mã trạng thái:</strong> {tax.status || '-'}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Lần kiểm tra thuế cuối</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.last_checked_tax ? 
                      new Date(formData.last_checked_tax).toLocaleString('vi-VN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                      }) : ''} 
                    readOnly={!isEditMode}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Additional Information */}
            <Row className="mb-4">
              <Col>
                <h5 className="text-primary">Thông tin bổ sung</h5>
                <hr />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md="6">
                <Form.Group>
                  <Form.Label>Nguồn</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.source || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('source', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Trạng thái Etsy</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={isEditMode ? (formData.info_etsy_status || '') : this.mapEtsyStatus(formData.info_etsy_status)} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('info_etsy_status', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md="6">
                <Form.Group>
                  <Form.Label>MID người tin cậy</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.ref_trusted_person_mid || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('ref_trusted_person_mid', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group>
                  <Form.Label>Ghi chú</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows="3"
                    value={formData.note || ''} 
                    readOnly={!isEditMode}
                    onChange={isEditMode ? (e) => this.handleInputChange('note', e.target.value) : undefined}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* C3D Photos */}
            {formData.c3d_photos && formData.c3d_photos.length > 0 && (
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Ảnh C3D ({formData.c3d_photos.length} ảnh)</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {formData.c3d_photos.map((photo, index) => (
                        <Badge key={index} bg="info" style={{ fontSize: '0.8em' }}>
                          Ảnh {index + 1}
                        </Badge>
                      ))}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* C3D Metadata */}
            {formData.c3d_metadata && (
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Metadata C3D</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows="4"
                      value={JSON.stringify(formData.c3d_metadata, null, 2)} 
                      readOnly={!isEditMode}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Đóng
          </Button>
          {isEditMode && onSave && (
            <Button variant="primary" onClick={() => onSave(formData)}>
              Lưu thay đổi
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default C3DInfoModal;
